import React, { useState, useEffect } from 'react';
import { Box, Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useFormContext, Controller } from 'react-hook-form';
import { useQuery } from '@apollo/client';

import TextInput from '/packages/shared-components/forms/TextInput/TextInput';
import SelectInput from '/packages/shared-components/forms/SelectInput/SelectInput';
import AutocompleteInput from '/packages/shared-components/forms/AutocompleteInput/AutocompleteInput';
import { useTheme } from '@mui/material/styles';
import { ErrorMessage } from '/packages/shared-components/ErrorMessage/ErrorMessage';
import { getBackgroundColor } from '/packages/shared-components/utils/color';
import { FormValues } from '../../test-plan-details/internal-view/test-plan-details-edit/TestPlanDetailsEdit/TestPlanDetailsEdit';
import { testPlanPropertiesForm } from '~src/components/api-integration/loaders';

import GET_PROVIDER_IDS from '~src/queries/codegen/getProviderIds';
import GET_API_TEST_PLATFORMS from '~src/queries/codegen/getApiTestPlatform';
import GET_API_VERSIONS from '~src/queries/codegen/getApiVersions';
import { Environment } from '~/src/generated/portal/graphql';
import { APITestPlan } from '~src/generated/portal/graphql';

export interface TestPlanPropertiesFormProps {
  partnerId: string;
  modal: boolean;
  testPlanDetail?: APITestPlan;
  isSaving?: boolean;
  apiVersions?: string[];
  nextStep?: () => void;
}

const TestPlanPropertiesForm = ({
  partnerId,
  modal,
  testPlanDetail,
  isSaving = false,
  nextStep,
}: TestPlanPropertiesFormProps) => {
  const theme = useTheme();
  const { control } = useFormContext<FormValues>();
  const { data: platformData, loading: loadingPlatforms, error: errorPlatforms } = useQuery(GET_API_TEST_PLATFORMS);
  const canShowModified = !modal && Boolean(testPlanDetail);

  const { data: providersData, loading: loadingProviders } = useQuery(GET_PROVIDER_IDS, {
    variables: { partnerId },
  });
  const { data: apiVersions, loading: loadingApiVersions } = useQuery(GET_API_VERSIONS);
  const [availablePlatforms, setAvailablePlatforms] = useState<string[]>([]);

  useEffect(() => {
    const newPlatforms = platformData?.apiTestPlatforms.map((platform) => platform.platform);
    setAvailablePlatforms(newPlatforms || []);
  }, [platformData]);

  let providerSelect =
    providersData?.providers.map((provider) => ({ label: provider.displayId, value: provider.displayId })) || [];
  if (providerSelect.length === 0) {
    providerSelect = [{ label: 'No providers found', value: '' }];
  }

  return (
    <Box
      sx={{
        width: '100%',
        overflowY: 'auto',
        marginTop: modal ? undefined : '20px',
      }}
      data-cy="test-plan-properties-form"
    >
      {loadingProviders || loadingPlatforms || loadingApiVersions ? (
        testPlanPropertiesForm
      ) : errorPlatforms ? (
        <ErrorMessage errorMsg={errorPlatforms.message} />
      ) : (
        <>
          <Typography variant="h6">Plan Properties</Typography>

          <Box sx={{ mb: 2, mt: 2 }}>
            <TextInput
              control={control}
              name="name"
              label="Plan Name"
              placeholder="Add a plan name"
              maxLength={50}
              required={true}
              showModified={canShowModified}
              disabled={isSaving}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextInput
              control={control}
              name="description"
              label="Description"
              placeholder="Add a plan description"
              required={false}
              rows={5}
              showModified={canShowModified}
              disabled={isSaving}
              hideMaxLengthAdornment
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <SelectInput
              control={control}
              name="providerId"
              placeholder="Select a provider ID"
              label="Provider ID"
              options={providerSelect}
              loading={loadingProviders}
              showModified={canShowModified}
              disabled={providerSelect[0].value === '' || isSaving}
              required={true}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <AutocompleteInput
              control={control}
              name="platform"
              options={availablePlatforms}
              showModified={canShowModified}
              label="Platform"
              placeholder="Select a platform"
              addNewOptionMessage="Press Enter to add a new platform"
              multiple={false}
              required={true}
              freeSolo={true}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <SelectInput
              control={control}
              name="environment"
              label="Environment"
              options={[
                {
                  label: 'Pre-production',
                  value: Environment.QA,
                },
                {
                  label: 'Production',
                  value: Environment.Prod,
                },
              ]}
              showModified={canShowModified}
              required={true}
              disabled={isSaving}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <SelectInput
              control={control}
              name="minApiVersion"
              label="API Version"
              options={apiVersions?.apiVersions.map((v) => ({ label: v.version, value: v.version })) || []}
              showModified={canShowModified}
              disabled={isSaving || !apiVersions?.apiVersions.length}
              placeholder={'Select a version'}
              required
            />
          </Box>
          {modal && (
            <>
              <Box
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: getBackgroundColor(theme.palette.primary),
                }}
              >
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    <Typography
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        marginBottom: 4,
                      }}
                    >
                      Plan Type
                    </Typography>
                  </FormLabel>
                  <Controller
                    name="planType"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field} aria-label="plan type" sx={{ marginLeft: '9px' }}>
                        <Box data-cy="plan-type-auto">
                          <FormControlLabel value={'auto'} control={<Radio />} label="Auto-Vending" />
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', ml: '32px', color: 'text.secondary', mt: '-8px', letterSpacing: 0 }}
                          >
                            Streamlines the setup by preloading gold standard tests and allowing quick SKU selection
                            from a dropdown.
                          </Typography>
                        </Box>
                        <Box data-cy="plan-type-manual">
                          <FormControlLabel value={'manual'} control={<Radio />} label="Manual" />
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', ml: '32px', color: 'text.secondary', mt: '-8px', letterSpacing: 0 }}
                          >
                            Configure test sets individually; SKUs must be entered manually afterwards.{' '}
                          </Typography>
                        </Box>
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Box>
              <Button variant="contained" sx={{ mt: '32px' }} onClick={nextStep} data-cy="next-step-btn">
                Next Step <ChevronRightIcon sx={{ fontSize: '20px' }} />
              </Button>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default TestPlanPropertiesForm;
